import { useCallback, useEffect, useReducer } from "react";
import { dataURLtoFile } from "@/lib/dataURLtoFile";
import { useWorkflowNavigation, WorkflowStatePathname } from "./useWorkflowNavigation";
import { usePathname } from "next/navigation";
import { WorkflowStateName } from "./WorkflowStateName";
import { Selfie } from "./Selfie";
import { createPlayer } from "@/lib/avatarSdkApi";
import { publishExperienceUserMessages, saveUserFromWorkflow } from "@/server/actions/user.actions";
import { useAvatarStatusPoller } from "./useAvatarStatusPoller";
import { uploadUserPhoto } from "@/lib/avatarService/avatarServiceApiClient";
import { uuidv7 } from "uuidv7";
import { getToken } from "@/lib/avatarService/avatarServiceToken";
import { useNFCSupport } from "@/components/NFC/useNFCSupport";
import { useNFCReader } from "@/components/NFC/useNFCReader";

const PathnameToAvatarWorkflowState: { [key: string]: WorkflowStateName } = {
    '/': WorkflowStateName.Initial,
    '/01-personal-details': WorkflowStateName.RegistrationStarted,
    '/02-selfie-instructions': WorkflowStateName.SelfieInstructions,
    '/03-selfie': WorkflowStateName.Selfie,
    '/04-selfie-viewer': WorkflowStateName.SelfieViewer,
    '/05-voice-recording': WorkflowStateName.VoiceRecording,
    '/all-finished': WorkflowStateName.AllSet,
};

export interface WorkflowState {
    stateName: WorkflowStateName;
    user?: User;
    selfieFile?: File,
    selfieDataURL?: string,
    playerUid?: string;
    avatarId?: string;
    avatar?: Avatar;
    voiceId?: string;
    avatarDownloadUrl?: string;
    pollingInterval: number | null
    error?: string
    nfcTagUid?: string | null;
    nfcScanStatus?: 'idle' | 'scanning' | 'success' | 'error';
    nfcUserNotFound?: boolean;
    voiceCloneSkipped?: boolean;
}

type AvatarRegistrationError = {}

export type User = {
    userUid?: string,
    profileId?: string,
    firstName?: string,
    lifeGoals?: string[],
    height?: number,
    gender?: string,
    bodyType?: 'S' | 'M' | 'L',
    nfcTagUid?: string
}

export interface Avatar {
    avatarId: string;
    url: string;
    status: string;
    progress: number;
    name: string;
    pipeline: string;
    description: string;
    createdOn: string;
    ctime?: string;
}

export type WorkflowAction =
    | { type: 'WORKFLOW_STARTED' }
    | { type: 'REGISTRATION_SUBMITTED'; user: User }
    | { type: 'REGISTRATION_FAILED', error?: AvatarRegistrationError }
    | { type: 'SELFIE_INSTRUCTIONS_ACCEPTED' }
    | { type: 'SELFIE_CREATED', selfie: Selfie }
    | { type: 'SELFIE_ACCEPTED' }
    | { type: 'USER_UID_GENERATED', userUid: string }
    | { type: 'SELFIE_UPLOAD_FAILED' }
    | { type: 'SELFIE_UPLOAD_SUCCEEDED', avatarId: string }
    | { type: 'AVATARSDK_PLAYER_CREATED', playerUid: string }
    | { type: 'AVATAR_STATUS_UPDATED', avatar: Avatar }
    | { type: 'AVATAR_COMPLETED', avatar: Avatar, avatarDownloadUrl?: string }
    | { type: 'AVATAR_FAILED', avatar: Avatar, error: string }
    | { type: 'RECORDING_FINISHED' }
    | { type: 'VOICE_CLONE_CREATED', voiceId: string }
    | { type: 'VOICE_CLONE_SKIPPED' }
    | { type: 'NFC_TAG_SCANNED', tagUid: string }
    | { type: 'NFC_SCAN_STARTED' }
    | { type: 'NFC_ERROR', error: string }
    | { type: 'NFC_USER_FETCHED', userData: { userUid?: string, firstName?: string, gender?: 'male' | 'female' | 'other', lifeGoals?: string[] } }
    | { type: 'NFC_USER_NOT_FOUND' }
    | { type: 'NFC_FETCH_ERROR', error?: string }
    | { type: 'BROWSER_NAVIGATION', expectedState: WorkflowStateName, currentUser?: User, playerUid?: string }
    | { type: 'STATE_LOADED_AFTER_BROWSER_NAVIGATION', loadedState: WorkflowState }
    | { type: 'RESET' }
    | { type: 'BACK' }
    | { type: 'USER_SAVED', userId: number }
    | { type: 'USER_SAVE_FAILED', error: string }
    ;

let defaultState: WorkflowState = {
    stateName: WorkflowStateName.Initial,
    pollingInterval: null,
    nfcScanStatus: 'idle',
    voiceCloneSkipped: false,
};

function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
    switch (action.type) {
        case 'WORKFLOW_STARTED':
            return { ...defaultState, stateName: WorkflowStateName.RegistrationStarted };
        case 'REGISTRATION_SUBMITTED':
            return {
                ...state, stateName: WorkflowStateName.RegistrationSubmitted, user: {
                    ...state.user,
                    ...action.user
                }
            };
        case 'REGISTRATION_FAILED':
            return { ...state, stateName: WorkflowStateName.RegistrationFailed };
        case 'AVATARSDK_PLAYER_CREATED':
            return { ...state, stateName: WorkflowStateName.SelfieInstructions, playerUid: action.playerUid };
        case 'SELFIE_INSTRUCTIONS_ACCEPTED':
            return { ...state, stateName: WorkflowStateName.Selfie };
        case 'SELFIE_CREATED':
            return {
                ...state,
                stateName: WorkflowStateName.SelfieViewer,
                selfieFile: action.selfie.photo,
                selfieDataURL: action.selfie.photoDataURL
            };
        case 'SELFIE_ACCEPTED':
            return {
                ...state,
                stateName: WorkflowStateName.SelfieUploading
            };
        case 'USER_UID_GENERATED':
            return { ...state, user: { ...state.user, userUid: action.userUid } };
        case 'SELFIE_UPLOAD_SUCCEEDED':
            return { ...state, stateName: WorkflowStateName.VoiceRecording, avatarId: action.avatarId, avatar: undefined, pollingInterval: 2000 };
        case 'AVATAR_STATUS_UPDATED':
            return { ...state, avatar: action.avatar }
        case 'AVATAR_COMPLETED':
            return { ...state, pollingInterval: null, avatarDownloadUrl: action.avatarDownloadUrl };
        case 'RECORDING_FINISHED':
            return { ...state, stateName: WorkflowStateName.VoiceRecordingUploading };
        case 'VOICE_CLONE_CREATED':
            return { ...state, stateName: WorkflowStateName.UserSaving, voiceId: action.voiceId, voiceCloneSkipped: false };
        case 'VOICE_CLONE_SKIPPED':
            return { ...state, stateName: WorkflowStateName.UserSaving, voiceId: '', voiceCloneSkipped: true };
        case "BROWSER_NAVIGATION":
            return {
                ...state,
                stateName: action.expectedState,
                playerUid: action.playerUid,
                user: {
                    ...state.user,
                    ...action.currentUser
                }
            };
        case "STATE_LOADED_AFTER_BROWSER_NAVIGATION":
            return { ...state, ...action.loadedState, pollingInterval: null };
        case 'BACK':
            return { ...state, stateName: getPreviousStep(state) };
        case "RESET":
            return defaultState;
        case 'USER_SAVED':
            return {
                ...state,
                stateName: WorkflowStateName.AllSet
            };
        case 'USER_SAVE_FAILED':
            return { ...state, stateName: WorkflowStateName.UserSaveFailed };
        case 'AVATAR_FAILED':
            return { ...state, stateName: WorkflowStateName.AvatarFailed, error: action.error };
        case 'NFC_SCAN_STARTED':
            return { ...state, nfcScanStatus: 'scanning', nfcUserNotFound: false };
        case 'NFC_TAG_SCANNED':
            return { ...state, nfcTagUid: action.tagUid, nfcScanStatus: 'success', user: state.user ? { ...state.user, nfcTagUid: action.tagUid } : undefined };
        case 'NFC_USER_FETCHED':
            return {
                ...state,
                nfcUserNotFound: false,
                user: state.user ? {
                    ...state.user,
                    userUid: action.userData.userUid || state.user.userUid,
                    firstName: action.userData.firstName || state.user.firstName,
                    gender: action.userData.gender || state.user.gender,
                    lifeGoals: action.userData.lifeGoals && action.userData.lifeGoals.length > 0
                        ? action.userData.lifeGoals
                        : state.user.lifeGoals
                } : {
                    userUid: action.userData.userUid,
                    firstName: action.userData.firstName,
                    gender: action.userData.gender,
                    lifeGoals: action.userData.lifeGoals
                }
            };
        case 'NFC_ERROR':
            return { ...state, nfcScanStatus: 'error' };
        case 'NFC_USER_NOT_FOUND':
            return { ...state, nfcUserNotFound: true };
        case 'NFC_FETCH_ERROR':
            return { ...state, nfcScanStatus: 'error', error: action.error };
        default:
            return defaultState;
    }
}

function getPreviousStep(workflow: WorkflowState): WorkflowStateName {
    switch (workflow.stateName) {
        case WorkflowStateName.Selfie:
            return WorkflowStateName.SelfieInstructions;
        case WorkflowStateName.SelfieViewer:
            return WorkflowStateName.Selfie;
    }

    return WorkflowStateName.RegistrationStarted;
}
export function useWorkflow() {
    let [workflow, dispatch] = useReducer(workflowReducer, defaultState);
    const {
        navigateToRegistration,
        navigateToState,
        navigateToSelfieInstructions,
        navigateToSelfie,
        navigateToSelfieViewer,
        navigateToHome,
        navigateToAllFinished
    } = useWorkflowNavigation();

    useAvatarStatusPoller({
        playerUid: workflow.playerUid,
        avatarId: workflow.avatarId,
        pollingInterval: workflow.pollingInterval,
        currentAvatarStatus: workflow.avatar?.status,
        dispatch
    });

    let pathname = usePathname();

    const { uploadPhotoToAvatarService } = useAvatarService();

    // NFC Support and Reader hooks
    const { nfcSupported, checkSupport: checkNFCSupport } = useNFCSupport();

    const handleNFCTagRead = useCallback(async (tagEvent: { tagUid: string; timestamp: Date }) => {
        dispatch({ type: 'NFC_TAG_SCANNED', tagUid: tagEvent.tagUid });

        // Fetch and populate user data when NFC tag is scanned
        const { getUserByNfcTag } = await import('@/server/api/dashboardApi');
        const result = await getUserByNfcTag(tagEvent.tagUid);

        if (result.success) {
            const user = result.user;

            // Dispatch user data to update workflow state
            dispatch({
                type: 'NFC_USER_FETCHED',
                userData: {
                    userUid: user.userUid,
                    firstName: user.firstName || '',
                    gender: user.gender as 'male' | 'female' | 'other' | undefined,
                    lifeGoals: user.customFields?.LifeGoals && Array.isArray(user.customFields.LifeGoals)
                        ? user.customFields.LifeGoals
                        : []
                }
            });
        } else if (result.notFound) {
            dispatch({ type: 'NFC_USER_NOT_FOUND' });
        } else {
            dispatch({ type: 'NFC_FETCH_ERROR', error: result.error });
        }
    }, []);

    const handleNFCError = useCallback(async (error: string | Error) => {
        const errorMessage = typeof error === 'string' ? error : error.message;
        dispatch({ type: 'NFC_ERROR', error: errorMessage });
    }, []);

    const {
        isScanning: isNFCScanning,
        loadingState: nfcLoadingState,
        statusMessage: nfcStatusMessage,
        startScanning: startNFCScan,
    } = useNFCReader({
        onTagRead: handleNFCTagRead,
        onError: handleNFCError
    });

    useEffect(() => {
        let statePathname = WorkflowStatePathname[workflow.stateName];

        if (pathname != statePathname) {
            console.log('Browser navigation happened (back, forward or reload), state: ' + WorkflowStateName[workflow.stateName]);
            console.log('Pathname: ' + pathname);
            console.log('StatePathname: ' + statePathname);

            // check if the current state is valid for the pathname
            let expectedState: WorkflowStateName = PathnameToAvatarWorkflowState[pathname];
            console.log('expectedState: ' + WorkflowStateName[expectedState]);

            let loadedState = retrieveStateFromSessionStorage();

            if (expectedState === WorkflowStateName.RegistrationStarted || expectedState === WorkflowStateName.Initial) {
                // no requirements for registration started state, but preserve user data if available
                dispatch({ type: 'BROWSER_NAVIGATION', expectedState, currentUser: loadedState?.user });
                return;
            }

            // User details must be present at further states
            if (!loadedState?.user?.firstName) {
                navigateToState(WorkflowStateName.RegistrationStarted);
                return;
            }

            if (expectedState === WorkflowStateName.SelfieInstructions) {
                // no requirements for registration started state, but preserve user data if available
                dispatch({
                    type: 'BROWSER_NAVIGATION',
                    expectedState,
                    currentUser: loadedState?.user,
                    playerUid: loadedState?.playerUid,
                });
                return;
            }

            // Selfie must be present at further states
            if (loadedState?.selfieFile == null && loadedState?.selfieDataURL == null) {
                navigateToState(WorkflowStateName.SelfieInstructions);
                return;
            }

            dispatch({ type: 'STATE_LOADED_AFTER_BROWSER_NAVIGATION', loadedState });
        }

    }, [pathname]);

    // Save state to session storage
    useEffect(() => {
        console.log('Workflow state changed:', workflow);
        if (workflow.stateName != WorkflowStateName.Initial
            && workflow.stateName != WorkflowStateName.RegistrationStarted) {
            saveStateToSessionStorage(workflow);
        }
    }, [workflow]);

    useEffect(() => {
        console.log('avatarDownloadUrl and/or voiceId changed:', workflow);
        if (workflow.stateName === WorkflowStateName.AllSet) {
            return;
        }

        if (workflow.avatarDownloadUrl && (workflow.voiceId || workflow.voiceCloneSkipped)) {
            console.log('Both avatar and voice clone are ready, saving user...');
            onBothAvatarAndVoiceCloneCreated()
                .catch(error => console.error(error));
        }
    }, [workflow.avatarDownloadUrl, workflow.voiceId]);

    async function startWorkflow() {
        dispatch({ type: 'WORKFLOW_STARTED' });
        navigateToRegistration();
    }

    async function onRegistrationSubmitted(user: User) {
        dispatch({ type: 'REGISTRATION_SUBMITTED', user });

        let result = await createPlayer();

        if (!result.success) {
            dispatch({ type: 'REGISTRATION_FAILED' });
            return;
        }

        dispatch({ type: 'AVATARSDK_PLAYER_CREATED', playerUid: result.data.code });

        navigateToSelfieInstructions();
    }

    function onSelfieInstructionsAccepted() {
        if (!dispatch) return;

        dispatch({ type: 'SELFIE_INSTRUCTIONS_ACCEPTED' });

        navigateToSelfie();
    }

    async function onSelfieCreated(selfie: Selfie) {
        dispatch({ type: 'SELFIE_CREATED', selfie });

        navigateToSelfieViewer();
    }

    async function onSelfieAccepted() {
        if (workflow.selfieFile == null) return;
        dispatch({ type: 'SELFIE_ACCEPTED' });

        try {
            // Only generate new userUid if not already present from previous steps
            let userUid = workflow.user?.userUid || uuidv7();
            const userForPublishing = {
                ...workflow.user,
                userUid,
            };
            if (!workflow.user?.userUid) {
                dispatch({ type: 'USER_UID_GENERATED', userUid });
            }

            let publishMessagePromise = publishExperienceUserMessages(userForPublishing);
            let uploadUserPhotoPromise = uploadPhotoToAvatarService(userUid, workflow.selfieFile);

            await Promise.all([uploadUserPhotoPromise, publishMessagePromise]);

            dispatch({ type: 'VOICE_CLONE_SKIPPED' });
            navigateToAllFinished();
        } catch (e) {
            console.error(e);
            dispatch({ type: 'SELFIE_UPLOAD_FAILED' });
        }
    }

    async function onBothAvatarAndVoiceCloneCreated() {
        let result = await saveUserFromWorkflow({
            user: workflow.user,
            voiceId: workflow.voiceId,
            avatarDownloadUrl: workflow.avatarDownloadUrl
        })

        if (!result.success) {
            dispatch({ type: 'REGISTRATION_FAILED' });
            return;
        }

        dispatch({ type: 'USER_SAVED', userId: result.userId });

        navigateToAllFinished();
    }


    function goBackToSelfie() {
        dispatch({ type: 'BACK' });
        navigateToSelfie();
    }

    const handleVoiceRecordingFinished = useCallback(() => {
        dispatch({ type: 'RECORDING_FINISHED' });
    }, [dispatch]);

    const handleVoiceCloneCreated = useCallback((voiceId: string) => {
        dispatch({ type: 'VOICE_CLONE_CREATED', voiceId });
    }, [dispatch]);

    const resetWorkflow = useCallback(() => {
        dispatch({ type: 'RESET' });

        navigateToHome();
    }, [dispatch]);

    const startNFCScanning = useCallback(async () => {
        // Check NFC support first
        if (!checkNFCSupport()) {
            return;
        }

        dispatch({ type: 'NFC_SCAN_STARTED' });
        await startNFCScan();
    }, [checkNFCSupport, startNFCScan]);

    return {
        workflow,
        dispatch,
        startWorkflow,
        onRegistrationSubmitted,
        onSelfieInstructionsAccepted,
        onSelfieCreated,
        onSelfieAccepted,
        handleVoiceRecordingFinished,
        handleVoiceCloneCreated,
        goBackToSelfie,
        resetWorkflow,
        startNFCScanning,
        nfcSupported,
        isNFCScanning,
        nfcLoadingState,
        nfcStatusMessage
    };
}

const avatarWorkflowStateKey = 'avatar-workflow-state';

function saveStateToSessionStorage(state: WorkflowState) {
    sessionStorage.setItem(avatarWorkflowStateKey, JSON.stringify(state));
}

function retrieveStateFromSessionStorage(): WorkflowState | null {
    const state = sessionStorage.getItem(avatarWorkflowStateKey);
    if (!state) return null;

    const parsedState = JSON.parse(state);

    if (parsedState.selfieDataURL == null) {
        return parsedState;
    }

    let file = dataURLtoFile(parsedState.selfieDataURL, 'photo.jpg');

    if (file != null) {
        parsedState.selfieFile = file;
    }

    return parsedState;
}

function useAvatarService(): { uploadPhotoToAvatarService: any; } {
    return {
        uploadPhotoToAvatarService: async (userUid: string, file: Blob | File, fileName?: string) => {
            const token = await getToken();

            if (!token) {
                throw new Error("Failed to retrieve token");
            }

            return await uploadUserPhoto(userUid, file, fileName, token);
        }
    };
}

